import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { ArrowRight, Bike, Car, Train, Dog, ExternalLink } from "lucide-react";
import { LOCATION, PARKING_DE_NEKKER_URL } from "../data/site";

const BEREIKBAARHEID_HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/j3n1ip24_Bereikbaarheid.jpg";

export default function Bereikbaarheid() {
  return (
    <>
      <Seo
        title="Bereikbaarheid | Mechelen Beach 2026"
        description="Hoe bereik je Mechelen Beach 2026 op het Douaneplein / Transit M? Te voet, met de fiets, het openbaar vervoer of de auto — alle praktische info."
      />

      <PageHero
        eyebrow="Praktische info"
        title="Bereikbaarheid"
        subtitle="Vlot, veilig en duurzaam tot aan het zand: hier ontdek je hoe je Mechelen Beach het best bereikt."
        image={BEREIKBAARHEID_HERO_IMAGE}
        imageAlt="Mechelen Beach op het Douaneplein / Transit M"
        primaryCta={{ label: "Contacteer ons", to: "/contact" }}
        secondaryCta={{ label: "Bekijk programma", to: "/programma" }}
      />

      <section className="bg-[#fdf8ec] py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Met de fiets */}
          <div data-testid="bereik-fiets" className="bg-white rounded-2xl border border-[#ebd9b5] p-7 sm:p-10">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-orange-gradient text-white shrink-0">
                <Bike className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2b1c10] leading-tight">
                  Met de fiets of te voet
                </h2>
                <p className="mt-4 text-base text-[#6b5945] leading-relaxed">
                  Dankzij de recente aanleg van de Tangent is Transit M erg vlot te bereiken.
                  We raden iedereen aan om met de fiets naar het evenement te komen. Zo ben je zeker
                  dat je geen parkeerplek moet zoeken en geraak je ook veilig thuis vanuit de bar.
                </p>
              </div>
            </div>
          </div>

          {/* Parkeren */}
          <div data-testid="bereik-auto" className="bg-white rounded-2xl border border-[#ebd9b5] p-7 sm:p-10">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-orange-gradient text-white shrink-0">
                <Car className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2b1c10] leading-tight">
                  Met de auto & parkeren
                </h2>
                <p className="mt-4 text-base text-[#6b5945] leading-relaxed">
                  Kom je met de auto? Dan raden we aan om te parkeren aan Parking De Nekker.
                  Van daaruit wandel je vlot naar Mechelen Beach op het Douaneplein / Transit M.
                </p>

                <a
                  href={PARKING_DE_NEKKER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bereik-parking-link"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#ed6a1a] hover:text-[#c54508] transition-colors"
                >
                  Bekijk Parking De Nekker
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>

                <div className="mt-6 rounded-xl bg-[#fdf3e3] border border-[#ebd9b5] p-4">
                  <p className="text-sm text-[#6b5945] leading-relaxed">
                    <span className="font-semibold text-[#2b1c10]">Belangrijk:</span> op het strand
                    zelf kan je uiteraard niet parkeren. Parkeren voor de ingang is niet toegestaan,
                    zodat deze zone vrij blijft voor leveranciers en hulpdiensten.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trein */}
          <div data-testid="bereik-trein" className="bg-white rounded-2xl border border-[#ebd9b5] p-7 sm:p-10">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-orange-gradient text-white shrink-0">
                <Train className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2b1c10] leading-tight">
                  Met de trein
                </h2>
                <p className="mt-4 text-base text-[#6b5945] leading-relaxed">
                  Station Mechelen bevindt zich op ongeveer 1,2 km wandelen van het evenement.
                  Station Mechelen-Nekkerspoel bevindt zich op ongeveer 600 meter wandelen.
                </p>
              </div>
            </div>
          </div>

          {/* Honden */}
          <div data-testid="bereik-honden" className="bg-white rounded-2xl border border-[#ebd9b5] p-7 sm:p-10">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-orange-gradient text-white shrink-0">
                <Dog className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#2b1c10] leading-tight">
                  Honden welkom op Mechelen Beach
                </h2>
                <p className="mt-4 text-base text-[#6b5945] leading-relaxed">
                  Ook je trouwe viervoeter is welkom bij ons op het strand. Zolang je hond
                  aangelijnd is en andere bezoekers niet stoort, mag hij gezellig mee genieten van
                  de zomerse sfeer.
                </p>
                <img
                  src="https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/6gnw0mqt_Blik%20op%20de%20zomerse%20sfeer.png"
                  alt="Blik op de zomerse sfeer van Mechelen Beach"
                  loading="lazy"
                  className="mt-6 rounded-xl shadow-sm object-cover w-full max-h-[420px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sand-2 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="eyebrow">Locatie</span>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-[#2b1c10]">
                {LOCATION.name}
              </h2>
              <p className="mt-4 text-base text-[#6b5945]">
                {LOCATION.postal} {LOCATION.city}, {LOCATION.country}
              </p>
              <a
                href={LOCATION.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bereik-maps-cta"
                className="mt-7 btn-primary"
              >
                Open in Google Maps
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#ebd9b5] shadow-[0_18px_40px_-16px_rgba(43,28,16,0.2)]">
              <iframe
                title="Google Maps — Mechelen Beach"
                src={LOCATION.mapsEmbedUrl}
                className="w-full h-[340px] sm:h-[400px] border-0 block"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
